import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'

type Props = {
  /**
   * タイトル (= 「{対象種類名}の削除」、 例: 「BG の削除」)。 呼び出し側が対象の種類名を入れて渡す
   * (docs/specs/conventions.md「削除確認ダイアログ」: 汎用の「削除の確認」 にしない。 #9313)。
   */
  title: string
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
  entityName: string
  errorMessage?: string
  /**
   * errorMessage が set された時に「dialog 内から再試行して意味があるか」を伝える。
   * - true (再試行可能): 一時的失敗 (Unavailable / Internal / 通信エラー等)。
   *   削除ボタンとキャンセルボタンを維持して即座に retry できるようにする。
   * - false / 未指定 (再試行不可): FK 参照ブロック / 楽観ロック conflict /
   *   対象消失。 削除ボタンを非表示、 キャンセルボタンの label を「閉じる」
   *   に切替。
   *
   * caller は deleteError.ts の `isRetriableDeleteError(err)` を呼んで
   * `mapMasterDeleteError(...)` と対で state に持つ (`{ message, retriable }`
   * pattern)。 未指定時は保守的に「再試行不可」扱いで dialog をロックする。
   */
  errorIsRetriable?: boolean
}

export const DeleteConfirmDialog = ({
  title,
  isOpen,
  onConfirm,
  onCancel,
  isLoading,
  entityName,
  errorMessage,
  errorIsRetriable,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // 「削除できない状態が確定した」= errorMessage が set かつ再試行不可な
  // ケース (isRetriableDeleteError=false → errorIsRetriable=false)。 同じ
  // dialog で再度 delete を試行してもサーバー側で同じエラーで弾かれるため、
  // 削除ボタンを非表示にしキャンセルボタンの label を「閉じる」に切替える
  // (index.md AC-MOI-009 の UX 補完)。
  // 一時的失敗 (errorIsRetriable=true) では削除ボタンを維持し、 ユーザーが
  // 同じ dialog から即座に retry できるようにする (codex 指摘 の regression
  // 防止、 PR #4456 review)。
  const isNonRetriableError =
    errorMessage !== undefined && errorIsRetriable !== true

  return (
    <ConfirmDialog
      body={
        errorMessage ?? t('common:deleteConfirmDialog.body', { entityName })
      }
      cancelLabel={isNonRetriableError ? t('common:action.close') : undefined}
      confirmLabel={t('common:action.delete')}
      destructive
      hideConfirm={isNonRetriableError}
      isLoading={isLoading}
      isOpen={isOpen}
      onCancel={onCancel}
      onConfirm={onConfirm}
      title={title}
    />
  )
}
